import { useStore } from '@/state/store';
import { fmtCurrency, fmtPct, fmtRatioPct, toDisplay } from '@/lib/format';
import { InfoTooltip } from './InfoTooltip';

type Tone = 'neutral' | 'safe' | 'caution' | 'danger';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700',
  safe: 'bg-safe-bg border-safe/40 text-slate-900',
  caution: 'bg-caution-bg border-caution/40 text-slate-900',
  danger: 'bg-danger-bg border-danger/50 text-slate-900',
};

function Kpi({ title, value, sub, tone = 'neutral', info }: { title: string; value: string; sub?: string; tone?: Tone; info: string }) {
  return (
    <div className={`flex flex-col rounded-lg border p-3 shadow-sm break-inside-avoid ${TONE_CLASS[tone]}`}>
      <div className="flex items-center text-xs font-medium text-slate-500 dark:text-slate-400">
        <span>{title}</span>
        <InfoTooltip text={info} label={title} />
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
    </div>
  );
}

/** Headline KPI cards driven by the live projection summary + estate (§10.3). */
export function KpiCards() {
  const result = useStore((s) => s.result);
  const showReal = useStore((s) => s.ui.showRealDollars);
  const inflation = useStore((s) => s.inputs.inflationAnnualPct);
  const months = useStore((s) => s.inputs.years * 12);

  if (!result) return null;
  const { summary, estate } = result;
  const disp = (n: number) => fmtCurrency(toDisplay(n, months, showReal, inflation));

  const cushionTone: Tone =
    summary.minCallCushionPct >= 0.4 ? 'safe' : summary.minCallCushionPct >= 0.15 ? 'caution' : 'danger';
  const callTone: Tone = summary.firstMaintenanceCallMonth !== null ? 'danger' : 'safe';
  const netWorthTone: Tone = summary.endingNetWorth < 0 ? 'danger' : 'neutral';

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      <Kpi title="Starting APR" value={fmtPct(summary.startApr)} info="Your loan's annual rate at the start = base rate + spread." />
      <Kpi title="After-tax APR" value={fmtPct(summary.afterTaxApr)} info="Your effective loan cost after any interest deduction. Compare it to your expected return." />
      <Kpi title="Effective spread" value={fmtPct(summary.effectiveSpread)} info="The margin your lender adds over the base rate, set at origination and fixed for the life of the line." />
      <Kpi title="Ending balance" value={disp(summary.endingBalance)} info="What you owe at the end of the horizon." />
      <Kpi title="Ending portfolio" value={disp(summary.endingPortfolio)} info="Projected value of your pledged investments at the horizon." />
      <Kpi title="Ending net worth" value={disp(summary.endingNetWorth)} tone={netWorthTone} info="Portfolio minus loan (plus any deployed proceeds). The bottom line of the strategy." />
      <Kpi title="Total interest" value={disp(summary.totalInterest)} info="Cumulative interest paid over the whole horizon — the price of carrying the loan." />
      <Kpi title="Worst LTV" value={fmtRatioPct(summary.worstLtv)} info="The highest loan-to-collateral ratio reached. Closer to 100% is riskier." />
      <Kpi title="Min call cushion" value={fmtRatioPct(summary.minCallCushionPct)} tone={cushionTone} info="The smallest market drop that would trigger a maintenance call across the horizon." />
      <Kpi
        title="First call month"
        value={summary.firstMaintenanceCallMonth !== null ? `Month ${summary.firstMaintenanceCallMonth}` : 'None'}
        tone={callTone}
        info="The first month a maintenance call is triggered, if any."
      />
      <Kpi
        title="Break-even"
        value={summary.carryIsAccretive ? 'Accretive ✓' : 'Erodes ✗'}
        sub={`Break-even return ${fmtPct(summary.breakEvenReturnPct)}`}
        tone={summary.carryIsAccretive ? 'safe' : 'caution'}
        info="Green when your assumed return beats your after-tax loan rate, so carrying the loan builds wealth."
      />
      <Kpi
        title="Estate difference"
        value={disp(estate.difference)}
        sub={estate.difference >= 0 ? 'Borrow leaves more to heirs' : 'Selling leaves more'}
        tone={estate.difference >= 0 ? 'safe' : 'neutral'}
        info="Heirs' value under borrow vs sell. Borrowing can win because a step-up in basis may erase the lifetime gain."
      />
    </div>
  );
}
